import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Target,
  CheckSquare,
  ListTodo,
  TrendingUp,
  Clock,
  ArrowUpRight,
  Building2,
  Calendar,
  AlertCircle,
  Globe,
  CircleDot,
  CircleCheck,
  TriangleAlert,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    className={onClick ? 'cursor-pointer' : ''}
    onClick={onClick}
  >
    <Card className="gds-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">{label}</p>
            <p className="text-3xl font-bold font-heading">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600 dark:text-${color}-400`} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default function ProjectManagerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeProjects: 0,
    pendingApprovals: 0,
    tasksInProgress: 0,
    overdueTasks: 0,
    tasksDone: 0,
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch clients from execution dashboard
      const executionRes = await api.get('/execution/dashboard');
      const clientsList = executionRes.data?.clients || [];
      setClients(clientsList);
      
      // Fetch tasks
      let tasksList = [];
      try {
        const tasksRes = await api.get('/tasks');
        tasksList = Array.isArray(tasksRes.data) ? tasksRes.data : (tasksRes.data?.items || []);
      } catch (e) {
        // Tasks might be empty
      }
      setTasks(tasksList);

      // Fetch pending approvals count
      let pendingApprovals = 0;
      try {
        const approvalsRes = await api.get('/approvals/pending');
        pendingApprovals = approvalsRes.data?.total ?? approvalsRes.data?.items?.length ?? approvalsRes.data?.length ?? 0;
      } catch (e) {
        // Approvals might be empty
      }

      // Fetch upcoming events
      try {
        const eventsRes = await api.get('/calendar/upcoming', { params: { days: 7 } });
        setUpcomingEvents(eventsRes.data?.slice(0, 3) || []);
      } catch (e) {
        // Calendar might be empty
      }

      const now = new Date();
      const overdueTasks = tasksList.filter(
        t => t.due_date && new Date(t.due_date) < now && t.status !== 'done'
      ).length;

      setStats({
        totalClients: clientsList.length,
        activeProjects: clientsList.filter(c => c.onboarding_complete).length,
        pendingApprovals,
        tasksInProgress: tasksList.filter(t => t.status === 'in_progress').length,
        overdueTasks,
        tasksDone: tasksList.filter(t => t.status === 'done').length,
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pm-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Project Manager Dashboard</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage clients, tasks, and approvals
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard/tasks')}>
            <ListTodo className="w-4 h-4 mr-2" />
            All Tasks
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard/calendar')}>
            <Calendar className="w-4 h-4 mr-2" />
            Calendar
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          icon={Users} 
          label="Total Clients" 
          value={stats.totalClients}
          color="indigo"
          onClick={() => navigate('/dashboard/execution')}
        />
        <StatCard 
          icon={Target} 
          label="Active Projects" 
          value={stats.activeProjects}
          color="cyan"
        />
        <StatCard 
          icon={CheckSquare} 
          label="Pending Approvals" 
          value={stats.pendingApprovals}
          color="amber"
          onClick={() => navigate('/dashboard/approvals')}
        />
        <StatCard 
          icon={ListTodo} 
          label="Tasks In Progress" 
          value={stats.tasksInProgress}
          color="violet"
          onClick={() => navigate('/dashboard/tasks')}
        />
      </div>

      {/* Task Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'In Progress', value: stats.tasksInProgress, icon: CircleDot, color: 'blue' },
          { label: 'Done', value: stats.tasksDone, icon: CircleCheck, color: 'emerald' },
          { label: 'Overdue', value: stats.overdueTasks, icon: TriangleAlert, color: stats.overdueTasks > 0 ? 'red' : 'slate' },
          { label: 'Onboarding', value: clients.filter(c => !c.onboarding_complete).length, icon: Users, color: 'violet' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`p-4 rounded-xl bg-${color}-50 dark:bg-${color}-900/20 flex items-center gap-3`}>
            <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400 shrink-0`} />
            <div>
              <p className={`text-xl font-bold font-heading text-${color}-700 dark:text-${color}-300`}>{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Clients List */}
        <Card className="lg:col-span-2 gds-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-500" />
                Client Overview
              </CardTitle>
              <CardDescription>{clients.length} total clients</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/execution')}>
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">No clients yet</p>
                <p className="text-sm mt-1">Clients will appear here after they register</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clients.slice(0, 5).map((client, i) => (
                  <motion.div
                    key={client.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    onClick={() => navigate('/dashboard/execution')}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-indigo-100 text-indigo-600">
                          {(client.name || '?').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {client.company_name && (
                            <span className="text-sm text-slate-500">{client.company_name}</span>
                          )}
                          {client.website && (
                            <Badge variant="outline" className="text-xs">
                              <Globe className="w-3 h-3 mr-1" />
                              Connected
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium">{client.tasks_completed}/{client.tasks_total} tasks</p>
                        <Progress value={client.progress_percentage} className="h-1.5 w-20 mt-1" />
                      </div>
                      <Badge variant={client.onboarding_complete ? 'default' : 'secondary'}>
                        {client.onboarding_complete ? 'Active' : 'Onboarding'}
                      </Badge>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events & Quick Actions */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-500" />
                Upcoming (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No upcoming events</p>
                  <Button 
                    variant="link" 
                    className="mt-2 text-indigo-600"
                    onClick={() => navigate('/dashboard/calendar')}
                  >
                    Add Event
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map((event) => (
                    <div 
                      key={event.event_id}
                      className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
                    >
                      <p className="font-medium text-sm">{event.title}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(event.start_date).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Actions */}
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Needs Attention
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.overdueTasks > 0 && (
                <Button
                  variant="outline"
                  className="w-full justify-between border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => navigate('/dashboard/tasks')}
                >
                  <span className="flex items-center text-red-600">
                    <TriangleAlert className="w-4 h-4 mr-2" />
                    Overdue Tasks
                  </span>
                  <Badge className="bg-red-100 text-red-700">{stats.overdueTasks}</Badge>
                </Button>
              )}
              {stats.pendingApprovals > 0 && (
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => navigate('/dashboard/approvals')}
                >
                  <span className="flex items-center">
                    <CheckSquare className="w-4 h-4 mr-2 text-amber-500" />
                    Pending Approvals
                  </span>
                  <Badge variant="secondary">{stats.pendingApprovals}</Badge>
                </Button>
              )}
              {clients.filter(c => !c.onboarding_complete).length > 0 && (
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => navigate('/dashboard/execution')}
                >
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Clients Onboarding
                  </span>
                  <Badge variant="secondary">
                    {clients.filter(c => !c.onboarding_complete).length}
                  </Badge>
                </Button>
              )}
              {stats.overdueTasks === 0 && stats.pendingApprovals === 0 && clients.filter(c => !c.onboarding_complete).length === 0 && (
                <div className="text-center py-4 text-slate-500 text-sm">
                  <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-30 text-emerald-500" />
                  All caught up!
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Tasks */}
      <Card className="gds-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-violet-500" />
              Recent Tasks
            </CardTitle>
            <CardDescription>Latest task activity</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/tasks')}>
            View All
            <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="font-medium">No tasks yet</p>
              <p className="text-sm mt-1">Tasks will appear here when clients have approved work</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="pb-3 font-medium">Task</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {tasks.slice(0, 5).map((task) => (
                    <tr key={task.task_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-4 font-medium">{task.title}</td>
                      <td className="py-4">
                        <Badge variant="outline">{task.task_type || 'General'}</Badge>
                      </td>
                      <td className="py-4">
                        <Badge className={
                          task.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                          task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {task.status?.replace('_', ' ') || 'Todo'}
                        </Badge>
                      </td>
                      <td className="py-4 text-slate-600 text-sm">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
